import { FormEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Save } from 'lucide-react';

import ConsentStatusChip from '../../components/ConsentStatusChip';
import RelativeTime from '../../components/RelativeTime';
import SectionCard from '../../components/SectionCard';
import { Fact, Mono, SectionLabel } from '../../components/detailPrimitives';
import {
  SetTenantServiceAccountResult,
  TenantDetail,
  setTenantServiceAccount,
} from '../../api/tenantRegistry';
import { useTenantDetailQuery } from '../../hooks/useTenantDetail';
import { errorText } from '../../lib/errorText';
import { TEXT } from '../../theme/tokens';

// Service Accounts tab — FD-158 (ADR-023) + FD-440 (`FD-ADR-042`).
//
// DESIGN DIRECTION: the operator's STATEMENT beside the platform's REPORT.
//
// This tab carries two facts that `FD-ADR-042` §1 forbids merging. The operator
// declares WHICH account mints for this destination tenant
// (`bprtServiceAccountUpn`, an operator-set column); the hourly Graph probe
// reports WHETHER that account is present, holds its role, and is excluded from
// Conditional Access (`bprtServiceAccount`, derived). They are stacked in that
// order — declaration above report — because the probe reports on the
// declaration, and reversing them puts an answer above its question.
//
// THE COPY IS THE HARD PART, AND IT IS WHY THIS FILE CARRIES A STATE MACHINE.
// "Not configured" is three different situations demanding three different
// operator actions, and one string for all of them is how a WORKING deployment
// gets "fixed" by someone who thought it was broken:
//
//   using-default  — no per-tenant value; the deployment-wide default applies.
//                    Fine. Nothing to do. The copy NAMES the default, because an
//                    operator's next question is always "which default".
//   unconfigured   — no value anywhere. A wave activation will have nothing to
//                    tell the operator to sign in as (`mint-session` returns an
//                    empty UPN and the server refuses to invent one).
//   named-missing  — a UPN resolves and Graph cannot find it. That disagreement
//                    is a FEATURE, not a rendering bug: it is how a misnamed or
//                    unprovisioned account surfaces BEFORE a wave, which is the
//                    whole reason the column and the snapshot stay separate.
//
// The server makes the first two distinguishable by NEVER substituting the
// deployment default into `bprtServiceAccountUpn` — it stays `null`, and the
// probe's own `upn` is the resolved value. That asymmetry is load-bearing; a
// projection that helpfully filled the column in would collapse two states.
//
// Amber, not red, for the last two (FLUENT_UI_ADDENDUM triage rule): neither is
// a crash and both are "look at this". Red stays reserved for a real failure.

/** Which of the three "where does the minting UPN come from" situations we are in. */
export type MintingAccountSource =
  | { kind: 'set'; upn: string; setBy: string | null; setUtc: string | null }
  | { kind: 'using-default'; upn: string }
  | { kind: 'unconfigured' };

/**
 * Derived from the tenant view alone — no client-side guess and no second call.
 *
 * The per-tenant column is authoritative when present. When it is null the
 * deployment default may still resolve, and the only place its value appears is
 * the probe's own snapshot, because the probe asked about the account the server
 * resolved (`Resolve-BprtServiceAccountUpn`). A null column with no probed UPN is
 * the genuinely-nothing case.
 */
export function mintingAccountSource(tenant: TenantDetail): MintingAccountSource {
  const declared = tenant.bprtServiceAccountUpn?.trim();
  if (declared) {
    return {
      kind: 'set',
      upn: declared,
      setBy: tenant.bprtServiceAccountUpnSetBy,
      setUtc: tenant.bprtServiceAccountUpnSetUtc,
    };
  }
  const probed = tenant.bprtServiceAccount?.upn?.trim();
  if (probed) return { kind: 'using-default', upn: probed };
  return { kind: 'unconfigured' };
}

function CaExclusionBadge({ healthy }: { healthy: boolean | null }) {
  if (healthy === true) {
    return (
      <Badge appearance="tint" color="success">
        Healthy
      </Badge>
    );
  }
  if (healthy === false) {
    return (
      <Badge appearance="tint" color="danger">
        Not excluded
      </Badge>
    );
  }
  return (
    <Badge appearance="tint" color="subtle">
      Unknown
    </Badge>
  );
}

function PresenceBadge({ present }: { present: boolean }) {
  return present ? (
    <Badge appearance="tint" color="success">
      Present
    </Badge>
  ) : (
    <Badge appearance="tint" color="danger">
      Missing
    </Badge>
  );
}

export default function TenantServiceAccountsPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const query = useTenantDetailQuery(tenantId);
  const tenant = query.data;
  if (!tenant || !tenantId) return null;

  return <ServiceAccountsBody tenantId={tenantId} tenant={tenant} />;
}

function ServiceAccountsBody({ tenantId, tenant }: { tenantId: string; tenant: TenantDetail }) {
  const queryClient = useQueryClient();
  const source = mintingAccountSource(tenant);
  const isDestination = tenant.roles.some((role) => role.toLowerCase() === 'destination');
  const sa = tenant.bprtServiceAccount;

  // An account resolves but Graph could not find it. Distinct from every
  // "not configured" state and never rendered as one.
  const namedButMissing = source.kind !== 'unconfigured' && sa !== null && sa.present === false;

  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState<SetTenantServiceAccountResult | null>(null);

  const save = useMutation<SetTenantServiceAccountResult, unknown, string>({
    mutationFn: (upn) => setTenantServiceAccount(tenantId, upn),
    onSuccess: (result) => {
      // The PATCH already triggered the server's own registry refresh and returns
      // the re-probed detail, so priming the shared cache updates the health
      // snapshot BELOW in the same paint as the account above. Refetching instead
      // would let the two drift apart on screen for a round trip — the exact split
      // this tab exists to make legible.
      queryClient.setQueryData(['tenant', tenantId], result.tenant);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setSaved(result);
      setDraft('');
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const upn = draft.trim();
    if (!upn || save.isPending) return;
    setSaved(null);
    save.mutate(upn);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      className="flex flex-col gap-6"
    >
      <div>
        <SectionLabel>BPRT minting service account</SectionLabel>
        <p className="font-display text-sm text-zinc-600 leading-relaxed max-w-[65ch] -mt-2 mb-2">
          The account an administrator signs in as when activating a wave, to create the
          credential devices use to join this tenant. Only its identity and health appear here —
          AirBridge holds no password or key for it, so nothing can sign in as it unattended.
        </p>
      </div>

      <SectionCard className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <SectionLabel>Minting account for this tenant</SectionLabel>

          <SourceStatement source={source} />

          {namedButMissing && (
            <MessageBar intent="warning">
              <MessageBarBody>
                <MessageBarTitle>Named, but not found in the tenant</MessageBarTitle>
                The last Graph check could not find{' '}
                <span className="font-mono text-xs">{sa?.upn}</span> in this tenant. The account
                is recorded here but has not been provisioned, or the UPN is wrong — a wave
                activation would send an operator to sign in as an account that does not exist.
              </MessageBarBody>
            </MessageBar>
          )}

          {isDestination ? (
            <form onSubmit={onSubmit} className="flex max-w-[560px] flex-col gap-3">
              <Field
                label="Service account UPN"
                hint="A UPN in the destination tenant, never a credential. Saving re-checks the account immediately."
              >
                <Input
                  type="email"
                  value={draft}
                  placeholder={
                    source.kind === 'unconfigured'
                      ? 'flightdeck-bprt-mint@contoso.com'
                      : source.upn
                  }
                  onChange={(_, data) => setDraft(data.value)}
                  disabled={save.isPending}
                />
              </Field>
              <div>
                <Button
                  type="submit"
                  appearance="primary"
                  icon={<Save size={16} strokeWidth={1.5} />}
                  disabled={save.isPending || draft.trim().length === 0}
                >
                  {save.isPending
                    ? 'Saving…'
                    : source.kind === 'set'
                      ? 'Replace minting account'
                      : 'Set minting account'}
                </Button>
              </div>
            </form>
          ) : (
            <MessageBar intent="info">
              <MessageBarBody>
                <MessageBarTitle>Source tenants do not mint</MessageBarTitle>
                A minting account can only be set on a tenant carrying the{' '}
                <strong>destination</strong> role — the account lives in the tenant a device
                joins. This tenant has no destination role recorded, so no field is offered.
              </MessageBarBody>
            </MessageBar>
          )}

          {save.isError && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Could not set the minting account</MessageBarTitle>
                {errorText(save.error)}
              </MessageBarBody>
            </MessageBar>
          )}

          {saved !== null && (
            <MessageBar intent={saved.refreshed ? 'success' : 'warning'}>
              <MessageBarBody>
                {saved.refreshed ? (
                  <>
                    <MessageBarTitle>Minting account saved</MessageBarTitle>
                    Recorded by {saved.setBy} and re-checked against the destination tenant.
                  </>
                ) : (
                  <>
                    <MessageBarTitle>Saved — health not re-checked</MessageBarTitle>
                    {/* `refreshed: false` with a 200 is a SUCCESSFUL write whose
                        follow-on probe failed. Deliberately no retry affordance:
                        retrying would re-PATCH a write that already landed. Re-check
                        is the right control, and it already exists in the header. */}
                    The account was stored. The health snapshot below is still the previous
                    one — use <strong>Re-check</strong> above for a fresh Graph read.
                  </>
                )}
              </MessageBarBody>
            </MessageBar>
          )}
        </div>

        <div className="flex flex-col gap-4 border-t border-zinc-200 pt-6">
          <SectionLabel>Health — last Graph check</SectionLabel>
          {sa === null ? (
            <p className="max-w-[65ch] font-display text-sm" style={{ color: TEXT.secondary }}>
              No health snapshot yet. The registry refresh probes whichever account resolves for
              this tenant; with no account resolving there is nothing to probe.
            </p>
          ) : (
            <dl className="grid grid-cols-1 gap-x-16 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
              <Fact label="Probed account">
                <Mono>{sa.upn}</Mono>
              </Fact>
              <Fact label="Assigned role">
                <span className="text-zinc-800">
                  {sa.role || <span className="text-zinc-400">—</span>}
                </span>
              </Fact>
              <Fact label="Account">
                <PresenceBadge present={sa.present} />
              </Fact>
              <Fact label="CA / MFA exclusion">
                <CaExclusionBadge healthy={sa.caExclusionHealthy} />
              </Fact>
            </dl>
          )}
        </div>

        {/* A quick consent reference so operators diagnosing mint failures see both
            gates in one place. */}
        <div className="border-t border-zinc-200 pt-6">
          <Fact label="Tenant consent">
            <ConsentStatusChip status={tenant.consentStatus} />
          </Fact>
        </div>
      </SectionCard>
    </motion.div>
  );
}

/**
 * The three-state statement. Each branch names a different next action, which is
 * the entire reason they are not one string.
 */
function SourceStatement({ source }: { source: MintingAccountSource }) {
  if (source.kind === 'set') {
    return (
      <dl className="grid grid-cols-1 gap-x-16 gap-y-5 sm:grid-cols-3">
        <Fact label="Account">
          <Mono>{source.upn}</Mono>
        </Fact>
        <Fact label="Set by">
          {source.setBy ? (
            <span className="text-zinc-800">{source.setBy}</span>
          ) : (
            <span className="text-zinc-400">—</span>
          )}
        </Fact>
        <Fact label="Set">
          <RelativeTime iso={source.setUtc} emptyPlaceholder="—" />
        </Fact>
      </dl>
    );
  }

  if (source.kind === 'using-default') {
    return (
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>No account set — using the deployment default</MessageBarTitle>
          This tenant names no account, so the deployment default applies:{' '}
          <span className="font-mono text-xs">{source.upn}</span>. That is correct for a
          deployment with one destination tenant — set a per-tenant account only when this
          tenant mints with a different one.
        </MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <MessageBar intent="warning">
      <MessageBarBody>
        <MessageBarTitle>No minting account anywhere</MessageBarTitle>
        Neither this tenant nor the deployment default names an account, so activating a wave
        into this tenant cannot tell the operator who to sign in as. Provision the account per
        the onboarding runbook, then record its UPN here.
      </MessageBarBody>
    </MessageBar>
  );
}
